import { Button, Input, Popover } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CloseCircleFilled } from '@ant-design/icons';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const dateFormats = ['DD.MM.YYYY', 'DD/MM/YYYY'];

export type DateRangePickerProps = {
    endOfStatus?: boolean;
    fromValue?: Dayjs;
    toValue?: Dayjs;
    minDate?: Dayjs;
    maxDate?: Dayjs;
    onChange?: (dates: [Dayjs | null, Dayjs | null] | null) => void;
    disabled?: boolean;
    format?: string;
    style?: React.CSSProperties;
    className?: string;
    placeholder?: [string, string];
    mode?: 'edit' | 'create';
    id?: string
};

const styles = {
    daterangepicker: {
        width: '100%'
    },
    input: {
        width: '100%',
        textAlign: 'left',
        padding: '4px 11px',
        height: '32px',
        border: '1px solid #d9d9d9',
        borderRadius: '6px',
    },
    calendar: {
        width: 280,
        padding: 8
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8
    },
    weekdays: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        textAlign: 'center',
        marginBottom: 8
    },
    weekday: {
        padding: 4,
        color: 'rgba(0, 0, 0, 0.45)'
    },
    days: {
        display: 'flex',
        flexDirection: 'column'
    },
    week: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)'
    },
    day: {
        padding: 4,
        textAlign: 'center',
        cursor: 'pointer',
        borderRadius: 2,
        transition: 'all 0.3s'
    },
    daySelected: {
        backgroundColor: '#1890ff',
        color: 'white'
    },
    dayInRange: {
        backgroundColor: '#e6f7ff'
    },
    dayDisabled: {
        color: '#d9d9d9',
        cursor: 'not-allowed'
    },
    dayOtherMonth: {
        color: 'rgba(0, 0, 0, 0.25)'
    },
    inputContainer: {
        display: 'flex',
        gap: '8px',
        width: '100%'
    },
    inputWrapper: {
        position: 'relative',
        width: '50%',
        display: 'flex',
        alignItems: 'center'
    },
    dateInput: {
        width: '100%',
    },
    dayHover: {
        backgroundColor: '#f5f5f5'
    },
    clearIcon: {
        position: 'absolute',
        right: '8px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: 'rgba(0, 0, 0, 0.25)',
        fontSize: '12px',
        cursor: 'pointer',
        zIndex: 1,
        transition: 'color 0.3s',
        ':hover': {
            color: 'rgba(0, 0, 0, 0.45)'
        },
        backgroundColor: 'white',
        padding: '0 2px'
    }
} as const;

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
    endOfStatus,
    fromValue,
    toValue,
    minDate,
    maxDate,
    onChange,
    disabled = false,
    format = "DD.MM.YYYY",
    style,
    className,
    placeholder = ["Başlangıç Tarihi", "Bitiş Tarihi"],
    mode = 'create',
    id = null

}) => {
    const [isStartOpen, setIsStartOpen] = useState(false);
    const [isEndOpen, setIsEndOpen] = useState(false);
    const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(fromValue || null);
    const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(toValue || null);
    const [startInputValue, setStartInputValue] = useState(fromValue?.format(format) || '');
    const [endInputValue, setEndInputValue] = useState(toValue?.format(format) || '');
    const [startMonth, setStartMonth] = useState(fromValue || dayjs());
    const [endMonth, setEndMonth] = useState(toValue || dayjs());
    const [hoveredDay, setHoveredDay] = useState<string | null>(null);

    useEffect(() => {
        const adjustedFromValue = fromValue ? fromValue.startOf('day') : null;
        const adjustedToValue = toValue ? toValue.startOf('day') : null;

        setSelectedStartDate(adjustedFromValue);
        setSelectedEndDate(adjustedToValue);
        setStartInputValue(adjustedFromValue?.format(format) || '');
        setEndInputValue(adjustedToValue?.format(format) || '');
        setStartMonth(adjustedFromValue || dayjs());
        setEndMonth(adjustedToValue || dayjs());
    }, [fromValue, toValue, format]);

    const handleDateChange = (startDate: Dayjs | null, endDate: Dayjs | null) => {
        if (!onChange) return;

        const adjustedStartDate = startDate ? startDate.utc().startOf('day') : null;
        const adjustedEndDate = endDate ? (endOfStatus != null && !endOfStatus ? endDate.utc().startOf('day') : endDate.utc().endOf('day')) : null;

        onChange([adjustedStartDate, adjustedEndDate]);
    };

    const handleStartDateClick = (date: Dayjs) => {
        setSelectedStartDate(date);
        setStartInputValue(date.format('DD.MM.YYYY'));

        setEndMonth(date);

        if (selectedEndDate && date.isAfter(selectedEndDate)) {
            setSelectedEndDate(null);
            setEndInputValue('');
        }
        handleDateChange(date, selectedEndDate);

        setTimeout(() => {
            const endInput = document.querySelector(id ? `.${id}-sf-end-input` : '.sf-end-input') as HTMLInputElement;
            if (endInput) {
                endInput.focus();
                setIsStartOpen(false);
                setIsEndOpen(true);
            }
        }, 100);
    };

    const handleEndDateClick = (date: Dayjs) => {
        if (selectedStartDate && date.isBefore(selectedStartDate)) {
            return;
        }
        setSelectedEndDate(date);
        setEndInputValue(date.format('DD.MM.YYYY'));
        handleDateChange(selectedStartDate, date);
    };

    const getDaysInMonth = (date: Dayjs) => {
        const daysInMonth = date.daysInMonth();
        const firstDayOfMonth = date.startOf('month');
        const days: Dayjs[] = [];

        for (let i = 0; i < firstDayOfMonth.day(); i++) {
            days.push(firstDayOfMonth.subtract(firstDayOfMonth.day() - i, 'day'));
        }

        for (let i = 1; i <= daysInMonth; i++) {
            days.push(date.date(i));
        }

        const remainingDays = 42 - days.length;
        const lastDay = days[days.length - 1];
        for (let i = 1; i <= remainingDays; i++) {
            days.push(lastDay.add(i, 'day'));
        }

        return days;
    };

    const parseDateInput = (value: string): Dayjs | null => {
        // Gün girişi (sadece 2 rakam)
        if (/^\d{2}$/.test(value)) {
            const newDate = dayjs().date(parseInt(value));
            return newDate.isValid() ? newDate : null;
        }

        if (/^\d{2}[./]\d{2}$/.test(value)) {
            const [day, month] = value.split(/[./]/);
            const newDate = dayjs().month(parseInt(month) - 1).date(parseInt(day));
            return newDate.isValid() ? newDate : null;
        }

        for (const dateFormat of dateFormats) {
            const parsed = dayjs(value, dateFormat, true);
            if (parsed.isValid()) {
                return parsed;
            }
        }
        return null;
    };

    const handleStartInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        value = value.replace(/\//g, '.');
        setStartInputValue(value);

        const parsedDate = parseDateInput(value);
        if (parsedDate) {
            setSelectedStartDate(parsedDate);
            setStartMonth(parsedDate);
            setEndMonth(parsedDate);
            handleDateChange(parsedDate, selectedEndDate);
        }
    };

    const handleEndInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        value = value.replace(/\//g, '.');
        setEndInputValue(value);

        const parsedDate = parseDateInput(value);
        if (parsedDate) {
            setSelectedEndDate(parsedDate);
            setEndMonth(parsedDate);
            handleDateChange(selectedStartDate, parsedDate);
        }
    };

    const handleStartInputBlur = () => {
        if (!parseDateInput(startInputValue)) {
            setStartInputValue(selectedStartDate?.format(format) || '');
        }
    };

    const handleEndInputBlur = () => {
        if (!parseDateInput(endInputValue)) {
            setEndInputValue(selectedEndDate?.format(format) || '');
        }
    };

    const isDateInRange = (date: Dayjs) => {
        if (!selectedStartDate || !selectedEndDate) return false;
        return date.isAfter(selectedStartDate) && date.isBefore(selectedEndDate);
    };

    const isDateDisabled = (date: Dayjs, isEndCalendar: boolean = false) => {
        if (minDate && date.isBefore(minDate)) return true;
        if (maxDate && date.isAfter(maxDate)) return true;
        if (isEndCalendar && selectedStartDate && date.isBefore(selectedStartDate)) return true;
        return false;
    };

    const renderStartCalendar = () => {
        const days = getDaysInMonth(startMonth);
        const weeks: Dayjs[][] = [];

        for (let i = 0; i < days.length; i += 7) {
            weeks.push(days.slice(i, i + 7));
        }

        return (
            <div style={styles.calendar} className="sf-calendar">
                <div style={styles.header}>
                    <Button
                        type="text"
                        onClick={() => setStartMonth(startMonth.subtract(1, 'month'))}
                    >
                        {"<"}
                    </Button>
                    <span>{startMonth.format('MMMM YYYY')}</span>
                    <Button
                        type="text"
                        onClick={() => setStartMonth(startMonth.add(1, 'month'))}
                    >
                        {">"}
                    </Button>
                </div>
                <div style={styles.weekdays}>
                    {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map(day => (
                        <div key={day} style={styles.weekday}>{day}</div>
                    ))}
                </div>
                <div style={styles.days}>
                    {weeks.map((week, weekIndex) => (
                        <div key={weekIndex} style={styles.week}>
                            {week.map((date, dayIndex) => {
                                const isSelected = selectedStartDate?.isSame(date, 'day');
                                const isDisabled = isDateDisabled(date);
                                const isCurrentMonth = date.month() === startMonth.month();
                                const dateKey = date.format('YYYY-MM-DD');
                                const isHovered = hoveredDay === dateKey;

                                const dayStyle = {
                                    ...styles.day,
                                    ...(isSelected && styles.daySelected),
                                    ...(isDisabled && styles.dayDisabled),
                                    ...(!isCurrentMonth && styles.dayOtherMonth),
                                    ...(isHovered && !isSelected && !isDisabled && styles.dayHover),
                                };

                                return (
                                    <div
                                        key={dayIndex}
                                        style={dayStyle}
                                        onClick={() => !isDisabled && handleStartDateClick(date)}
                                        onMouseEnter={() => setHoveredDay(dateKey)}
                                        onMouseLeave={() => setHoveredDay(null)}
                                    >
                                        {date.date()}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderEndCalendar = () => {
        const days = getDaysInMonth(endMonth);
        const weeks: Dayjs[][] = [];

        for (let i = 0; i < days.length; i += 7) {
            weeks.push(days.slice(i, i + 7));
        }

        return (
            <div style={styles.calendar} className="sf-calendar">
                <div style={styles.header}>
                    <Button
                        type="text"
                        onClick={() => setEndMonth(endMonth.subtract(1, 'month'))}
                    >
                        {"<"}
                    </Button>
                    <span>{endMonth.format('MMMM YYYY')}</span>
                    <Button
                        type="text"
                        onClick={() => setEndMonth(endMonth.add(1, 'month'))}
                    >
                        {">"}
                    </Button>
                </div>
                <div style={styles.weekdays}>
                    {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map(day => (
                        <div key={day} style={styles.weekday}>{day}</div>
                    ))}
                </div>
                <div style={styles.days}>
                    {weeks.map((week, weekIndex) => (
                        <div key={weekIndex} style={styles.week}>
                            {week.map((date, dayIndex) => {
                                const isSelected = selectedEndDate?.isSame(date, 'day');
                                const isDisabled = isDateDisabled(date, true);
                                const isCurrentMonth = date.month() === endMonth.month();
                                const dateKey = date.format('YYYY-MM-DD');
                                const isHovered = hoveredDay === dateKey;

                                const dayStyle = {
                                    ...styles.day,
                                    ...(isSelected && styles.daySelected),
                                    ...(isDisabled && styles.dayDisabled),
                                    ...(!isCurrentMonth && styles.dayOtherMonth),
                                    ...(isHovered && !isSelected && !isDisabled && styles.dayHover),
                                };

                                return (
                                    <div
                                        key={dayIndex}
                                        style={dayStyle}
                                        onClick={() => !isDisabled && handleEndDateClick(date)}
                                        onMouseEnter={() => setHoveredDay(dateKey)}
                                        onMouseLeave={() => setHoveredDay(null)}
                                    >
                                        {date.date()}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const displayValue = () => {
        if (!selectedStartDate && !selectedEndDate) return placeholder.join(' - ');
        const start = selectedStartDate?.format(format);
        const end = selectedEndDate?.format(format);
        return `${start || ''} - ${end || ''}`;
    };

    const handleStartFocus = () => {
        setTimeout(() => {
            setIsStartOpen(true);
            setIsEndOpen(false);
        }, 0);
    };

    const handleEndFocus = () => {
        setTimeout(() => {
            setIsEndOpen(true);
            setIsStartOpen(false);
        }, 0);
    };

    const handleStartClick = (e: React.MouseEvent<HTMLInputElement>) => {
        e.preventDefault();
        setIsStartOpen(true);
        setIsEndOpen(false);
    };

    const handleEndClick = (e: React.MouseEvent<HTMLInputElement>) => {
        e.preventDefault();
        setIsEndOpen(true);
        setIsStartOpen(false);
    };

    const handleStartBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const relatedTarget = e.relatedTarget as HTMLElement;
        if (!relatedTarget?.closest('.sf-calendar')) {
            setTimeout(() => {
                if (!document.activeElement?.closest('.sf-calendar')) {
                    setIsStartOpen(false);
                }
            }, 200);
        }
        handleStartInputBlur();
    };

    const handleEndBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const relatedTarget = e.relatedTarget as HTMLElement;
        if (!relatedTarget?.closest('.sf-calendar')) {
            setTimeout(() => {
                if (!document.activeElement?.closest('.sf-calendar')) {
                    setIsEndOpen(false);
                }
            }, 200);
        }
        handleEndInputBlur();
    };

    const handleClearStart = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedStartDate(null);
        setStartInputValue('');
        handleDateChange(null, selectedEndDate);
    };

    const handleClearEnd = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedEndDate(null);
        setEndInputValue('');
        handleDateChange(selectedStartDate, null);
    };

    return (
        <div style={{ ...styles.daterangepicker, ...style }} className={className}>
            <div style={styles.inputContainer}>
                <div style={styles.inputWrapper}>
                    <Popover
                        content={renderStartCalendar()}
                        trigger="click"
                        open={isStartOpen}
                        onOpenChange={(open) => setIsStartOpen(open)}
                        placement="bottomLeft"
                        style={{ width: '100%' }}
                    >
                        <Input
                            style={styles.dateInput}
                            value={startInputValue}
                            onChange={handleStartInputChange}
                            onFocus={handleStartFocus}
                            onClick={handleStartClick}
                            onBlur={handleStartBlur}
                            placeholder={placeholder[0]}
                            disabled={disabled}
                            className="sf-start-input"
                        />
                    </Popover>
                    {startInputValue && !disabled && (
                        <CloseCircleFilled
                            style={styles.clearIcon}
                            onClick={handleClearStart}
                        />
                    )}
                </div>
                <div style={styles.inputWrapper}>
                    <Popover
                        content={renderEndCalendar()}
                        trigger="click"
                        open={isEndOpen}
                        onOpenChange={(open) => setIsEndOpen(open)}
                        placement="bottomLeft"
                    >
                        <Input
                            style={styles.dateInput}
                            value={endInputValue}
                            onChange={handleEndInputChange}
                            onFocus={handleEndFocus}
                            onClick={handleEndClick}
                            onBlur={handleEndBlur}
                            placeholder={placeholder[1]}
                            disabled={disabled}
                            className={id ? `${id}-sf-end-input` : '.sf-end-input'}
                        />
                    </Popover>
                    {endInputValue && !disabled && (
                        <CloseCircleFilled
                            style={styles.clearIcon}
                            onClick={handleClearEnd}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};